/**
 * Phase 4 slice 4 — EOT Claim Pack. Pick a project + window, preview the
 * client-attributable hindrances, and download the full PDF claim pack.
 */
import React, { useEffect, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiDownload } from '../../lib/api';
import { toast } from 'sonner';

import { formatDate as fmt } from '../../lib/format';

export default function EOTClaimPack() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [hindrances, setHindrances] = useState([]);
  const [totalDays, setTotalDays] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { apiGet('/projects').then((p) => { setProjects(p); if (p.length) setProjectId(p[0].id); }).catch(() => toast.error('Failed to load projects')); }, []);

  useEffect(() => {
    if (!projectId) return;
    apiGet(`/hindrances?project_id=${projectId}&responsibility=Client`).then((d) => {
      let rows = d.hindrances || [];
      if (from) rows = rows.filter((h) => (h.raised_at || '').slice(0, 10) >= from);
      if (to) rows = rows.filter((h) => (h.raised_at || '').slice(0, 10) <= to);
      setHindrances(rows);
      setTotalDays(rows.reduce((s, h) => s + (Number(h.impact_days) || 0), 0));
    }).catch(() => { setHindrances([]); setTotalDays(0); });
  }, [projectId, from, to]);

  const download = async () => {
    if (!projectId) { toast.error('Select a project'); return; }
    setDownloading(true);
    try {
      const proj = projects.find((p) => p.id === projectId);
      const q = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&');
      await apiDownload(`/projects/${projectId}/eot-claim-pack.pdf${q ? `?${q}` : ''}`, `EOT_Claim_${proj?.code || 'project'}.pdf`);
      toast.success('EOT claim pack downloaded');
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'No permission' : 'Failed to generate PDF');
    } finally { setDownloading(false); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader overline="Projects" title="EOT Claim Pack" subtitle="Extension-of-Time claim — client-attributable hindrances + schedule variance" />

      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4 flex flex-wrap items-end gap-3" data-testid="eot-controls">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[260px]" data-testid="eot-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="eot-from" />
        </div>
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="eot-to" />
        </div>
        <button onClick={download} disabled={downloading} className="inline-flex items-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-5 py-2.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium text-sm" data-testid="eot-download-btn">
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={15} />} Download Claim Pack (PDF)
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="kpi-overline">Client Hindrances</div><div className="text-3xl font-display font-extrabold text-slate-800 mt-1">{hindrances.length}</div></div>
        <div className="bg-white border border-red-200 rounded-sm p-4"><div className="kpi-overline text-red-700">Days Claimed</div><div className="text-3xl font-display font-extrabold text-red-600 mt-1">{totalDays.toFixed(1)}</div></div>
        <div className="bg-[#1e2540] text-white rounded-sm p-4 flex items-center gap-2"><FileText size={20} className="text-cyan-300" /><span className="text-xs text-slate-300">Pack includes cover, client hindrances, correspondence & baseline-vs-actual variance.</span></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Client-Attributable Hindrances (preview)</div>
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="eot-hindrance-table">
            <thead><tr><th>Raised</th><th>Hindrance</th><th className="text-right">Days</th><th>Status</th><th>Exp. Resolution</th></tr></thead>
            <tbody>
              {hindrances.length === 0 ? <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-10">No client-attributable hindrances in this window.</td></tr>
                : hindrances.map((h) => (
                  <tr key={h.id} data-testid={`eot-hindrance-${h.id}`}>
                    <td className="text-xs text-slate-500">{fmt(h.raised_at)}</td>
                    <td className="text-[13px] font-display font-semibold max-w-[300px] truncate">{h.title}</td>
                    <td className="text-right font-display font-bold">{h.impact_days}</td>
                    <td className="text-xs text-slate-600">{(h.status || '').replace('_', ' ')}</td>
                    <td className="text-xs text-slate-500">{fmt(h.expected_resolution_date)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
